import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Boxes, Plus, Search, X, Loader2, Trash2, Pencil, Settings2, ShieldCheck,
} from "lucide-react";

export default function Entities() {
  const { isAdmin, canWrite } = useAuth();
  const [schemaInfo, setSchemaInfo] = useState(null);
  const [activeTab, setActiveTab] = useState(null); // schema key
  const [records, setRecords] = useState([]);
  const [q, setQ] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showSchemas, setShowSchemas] = useState(false);
  const [enabledDraft, setEnabledDraft] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vertical/schemas");
      setSchemaInfo(data);
      setEnabledDraft(data.enabled || []);
      const active = data.active || [];
      if (active.length && !activeTab) setActiveTab(active[0].key);
    } catch {
      toast.error("Could not load schemas");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const current = (schemaInfo?.active || []).find((s) => s.key === activeTab);
  const dpdp = !!schemaInfo?.dpdp_mode;

  const loadRecords = useCallback(async () => {
    if (!current) return;
    if (dpdp && !reason.trim()) { setRecords([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/vertical/records", {
        params: { entity_type: current.entity_type, q: q || undefined, reason: reason || undefined },
      });
      setRecords(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load records");
    } finally {
      setLoading(false);
    }
  }, [current, q, reason, dpdp]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const saveEnabled = async () => {
    try {
      await api.post("/vertical/schemas/enable", { keys: enabledDraft });
      toast.success("Schemas updated");
      setShowSchemas(false);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteRecord = async (rid) => {
    const rReason = window.prompt("Reason for deletion (audit log):", "") || "";
    if (rReason === null) return;
    try {
      const { data } = await api.delete(`/vertical/records/${rid}`, { params: { reason: rReason } });
      if (data.pending) toast.success("Deletion requested — awaiting a second admin's approval");
      else toast.success("Record deleted");
      loadRecords();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="entities-page">
      <header className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="eyebrow mb-2">Vertical schemas</p>
          <h1 className="font-display text-2xl">Structured records &amp; cross-department search</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Patients, students, cases, assets — searchable in chat with{" "}
            <code className="text-xs bg-secondary/60 px-1.5 py-0.5 rounded">@{current?.entity_type || "entity"}</code>.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowSchemas(true)} className="rounded-full" data-testid="entities-schemas-btn">
              <Settings2 className="w-4 h-4 mr-1.5" /> Schemas
            </Button>
          )}
          {canWrite && current && (
            <Button onClick={() => { setEditing({ entity_type: current.entity_type, fields: {}, tags: [] }); setShowEditor(true); }}
              className="rounded-full" data-testid="entities-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New {current.entity_type}
            </Button>
          )}
        </div>
      </header>

      {dpdp && (
        <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-4 py-2.5 mb-4 flex items-center gap-2 text-sm" data-testid="entities-dpdp-banner">
          <ShieldCheck className="w-4 h-4 text-sky-500" />
          DPDP mode is on — a reason is logged with every read.
        </div>
      )}

      {schemaInfo?.active?.length === 0 ? (
        <div className="bento p-10 text-center" data-testid="entities-empty">
          <div className="empty-icon w-14 h-14 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 flex items-center justify-center mx-auto mb-4 text-fuchsia-500">
            <Boxes className="w-6 h-6 text-fuchsia-500" />
          </div>
          <p className="font-display text-xl mb-2">No schemas enabled</p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {isAdmin ? "Enable a vertical schema to start capturing structured records." : "Ask an admin to enable a vertical schema."}
          </p>
          {isAdmin && (
            <Button onClick={() => setShowSchemas(true)} className="rounded-full mt-6" data-testid="entities-empty-open-schemas">
              Choose schemas
            </Button>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="entities-tabs">
            {(schemaInfo?.active || []).map((s) => (
              <button key={s.key} onClick={() => setActiveTab(s.key)}
                data-testid={`entities-tab-${s.key}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider font-mono rounded-full border transition-colors ${
                  activeTab === s.key ? "bg-foreground text-background border-foreground" : "border-border/60 hover:border-foreground/40"
                }`}>
                {s.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" placeholder={`Search ${current?.entity_type || "records"}…`} data-testid="entities-search-input" />
            </div>
            {dpdp && (
              <Input value={reason} onChange={(e) => setReason(e.target.value)} className="min-w-[220px]"
                placeholder="Reason for viewing (required)" data-testid="entities-reason-input" />
            )}
          </div>

          {loading ? (
            <div className="space-y-2" data-testid="entities-skeleton" role="status" aria-label="Loading records">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bento p-4 flex items-start gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
                    <div className="flex gap-4">
                      <div className="h-3 w-24 rounded bg-muted/70 animate-pulse" />
                      <div className="h-3 w-32 rounded bg-muted/70 animate-pulse" />
                      <div className="h-3 w-20 rounded bg-muted/70 animate-pulse" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : records.length === 0 ? (
            <div
              className="bento p-10 text-center flex flex-col items-center gap-3 max-w-lg mx-auto"
              data-testid="entities-list-empty"
            >
              <div className="empty-icon w-12 h-12 rounded-full bg-fuchsia-500/10 border border-fuchsia-500/30 flex items-center justify-center text-fuchsia-500">
                <Boxes className="w-5 h-5 text-fuchsia-500" />
              </div>
              <div>
                <p className="font-display text-lg">
                  {dpdp && !reason.trim()
                    ? "Enter a reason to view records"
                    : q
                      ? `No ${current?.entity_type || "records"} match "${q}"`
                      : `No ${current?.entity_type || "records"} yet`}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {dpdp && !reason.trim()
                    ? "DPDP mode logs a reason with every read — the field above is required."
                    : q
                      ? "Try a shorter search, or clear the filter to see everything."
                      : `Structured ${current?.label?.toLowerCase() || "records"} you add here become searchable in chat with @${current?.entity_type || "entity"}.`}
                </p>
              </div>
              {!q && !(dpdp && !reason.trim()) && canWrite && current && (
                <Button
                  size="sm"
                  className="rounded-full mt-2"
                  onClick={() => { setEditing({ entity_type: current.entity_type, fields: {}, tags: [] }); setShowEditor(true); }}
                  data-testid="entities-empty-new-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> Add first {current.entity_type}
                </Button>
              )}
              {q && (
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full mt-2"
                  onClick={() => setQ("")}
                  data-testid="entities-empty-clear-btn"
                >
                  <X className="w-3.5 h-3.5 mr-1.5" /> Clear search
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2" data-testid="entities-list">
              {records.map((r) => (
                <div key={r.id} className="bento p-4 flex items-start gap-4" data-testid={`entities-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{r.fields.name || r.fields.title || r.entity_type}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                      {Object.entries(r.fields).slice(0, 5).map(([k, v]) => (
                        v && k !== "name" && k !== "title" ? (
                          <span key={k}><span className="text-foreground/60">{k}:</span> {String(v).slice(0, 60)}</span>
                        ) : null
                      ))}
                    </div>
                  </div>
                  {canWrite && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => { setEditing(r); setShowEditor(true); }}
                        className="p-2 text-muted-foreground hover:text-foreground rounded" data-testid={`entities-edit-${r.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button onClick={() => deleteRecord(r.id)}
                          className="p-2 text-muted-foreground hover:text-destructive rounded" data-testid={`entities-delete-${r.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Editor modal */}
      {showEditor && editing && current && (
        <EntityEditor schema={current} record={editing} dpdp={dpdp}
          onClose={() => { setShowEditor(false); setEditing(null); }}
          onSaved={() => { setShowEditor(false); setEditing(null); loadRecords(); }} />
      )}

      {/* Schemas modal */}
      {showSchemas && schemaInfo && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="entities-schemas-modal">
          <div className="bento w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-lg">Enable vertical schemas</h3>
              <button onClick={() => setShowSchemas(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2 mb-5">
              {Object.values(schemaInfo.builtin).map((s) => {
                const on = enabledDraft.includes(s.key);
                return (
                  <label key={s.key} className="flex items-start gap-3 cursor-pointer p-3 rounded border border-border/60 hover:border-foreground/40">
                    <input type="checkbox" checked={on} className="mt-0.5 accent-emerald-500"
                      onChange={() => setEnabledDraft((d) => on ? d.filter((k) => k !== s.key) : [...d, s.key])}
                      data-testid={`entities-schema-toggle-${s.key}`} />
                    <div>
                      <p className="text-sm font-medium">{s.label}</p>
                      <p className="text-[11px] text-muted-foreground">{s.fields.length} fields · @{s.entity_type}</p>
                    </div>
                  </label>
                );
              })}
            </div>
            <Button onClick={saveEnabled} className="rounded-full w-full" data-testid="entities-schemas-save">Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function EntityEditor({ schema, record, dpdp, onClose, onSaved }) {
  const [fields, setFields] = useState(record.fields || {});
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (record.id) {
        await api.patch(`/vertical/records/${record.id}`, { entity_type: schema.entity_type, fields, tags: record.tags || [] });
      } else {
        await api.post("/vertical/records", { entity_type: schema.entity_type, fields, tags: [] });
      }
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };
  const inputCls = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-1 focus:ring-ring";
  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="entities-editor-modal">
      <div className="bento w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-lg">{record.id ? "Edit" : "New"} {schema.entity_type}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-auto">
          {schema.fields.map((f) => (
            <div key={f.key}>
              <label className="eyebrow block mb-1.5">{f.label}{f.required && <span className="text-destructive"> *</span>}</label>
              {f.type === "textarea" ? (
                <textarea rows={4} className={`${inputCls} h-24 py-2 resize-none`} required={!!f.required}
                  value={fields[f.key] || ""} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                  data-testid={`entities-field-${f.key}`} />
              ) : (
                <input type={f.type === "date" ? "date" : "text"} className={inputCls} required={!!f.required}
                  value={fields[f.key] || ""} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })}
                  data-testid={`entities-field-${f.key}`} />
              )}
            </div>
          ))}
          <Button type="submit" className="rounded-full w-full mt-4" disabled={busy} data-testid="entities-editor-save">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save"}
          </Button>
        </form>
      </div>
    </div>
  );
}
